import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Film, Sparkles, Users, Star, ChevronLeft, ChevronRight, Calendar, Clock, Tv, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { mostWatchedApi, formatDuration, filmsApi, thumbUrl } from '../lib/api';
import ContentPreviewModal from '../components/ContentPreviewModal';
import CreatorProfileModal from '../components/CreatorProfileModal';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mostWatched, setMostWatched] = useState([]);
  const [comingSoon, setComingSoon] = useState({ movies: [], series: [] });
  const [previewFilm, setPreviewFilm] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const scrollRef = useRef(null);
  const bgRef = useRef(null);
  const fgRef = useRef(null);

  // Smooth parallax via refs + rAF (adaptive intensity for mobile)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const bgSpeed = isMobile ? 0.15 : 0.35;
    const fgSpeed = isMobile ? -0.05 : -0.12;
    let rafId = null;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      if (bgRef.current) bgRef.current.style.transform = 'translate3d(0,' + y * bgSpeed + 'px,0)';
      if (fgRef.current) fgRef.current.style.transform = 'translate3d(0,' + y * fgSpeed + 'px,0)';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { rafId = requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  useEffect(() => {
    mostWatchedApi.get().then(d => setMostWatched(d.films || [])).catch(() => {});
    filmsApi.getComingSoon().then(d => setComingSoon(d)).catch(() => {});
  }, []);

  const features = [
    { icon: <Film className="w-8 h-8" />, title: 'AI-Generated Films', description: 'Discover cutting-edge films created entirely with AI technology' },
    { icon: <Sparkles className="w-8 h-8" />, title: 'Curated Collection', description: 'Hand-picked selection of the best AI cinema from top creators' },
    { icon: <Users className="w-8 h-8" />, title: 'The Global Home of AI Cinema', description: 'Watch original films created by independent storytellers from around the world.' },
  ];

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative min-h-[80vh] sm:h-[700px] md:h-[85vh] md:max-h-[900px] flex items-center overflow-hidden">
        <div className="absolute inset-0" ref={bgRef} style={{ willChange: 'transform' }}>
          <div className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/hero-bg.jpg)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030014]/90 via-[#030014]/60 to-[#030014]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/30 to-[#030014]/40" />
        </div>

        {/* Navbar */}
        <nav className="absolute top-0 left-0 right-0 z-20">
          <div className="w-full px-4 md:px-8 lg:px-[4%] py-6">
            <div className="flex items-center justify-between">
              <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
              <div className="flex items-center gap-4">
                {isAuthenticated ? (
                  <Link to="/browse" className="btn-primary" data-testid="browse-button">Browse</Link>
                ) : (
                  <Link to="/login" className="btn-primary" data-testid="signin-button">Sign In</Link>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-[4%] pt-20 flex justify-center" ref={fgRef} style={{ willChange: 'transform' }}>
          <div className="max-w-2xl text-left">
            <div className="flex items-center gap-2 mb-6">
              <span className="badge bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30">AI Cinema</span>
              <span className="badge">Premium</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-4 sm:mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="hero-title">
              The Future of<br />
              <span className="glow-text text-[#8B5CF6]">AI Cinema</span>
            </h1>
            <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed" data-testid="hero-subtitle">
              Experience groundbreaking films created entirely with artificial intelligence.
              From experimental shorts to full-length features, discover the new frontier of filmmaking.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              {isAuthenticated ? (
                <Link to="/browse" className="btn-white flex items-center gap-2 text-lg" data-testid="start-watching-button">
                  <Play className="w-5 h-5 fill-current" /> Start Watching
                </Link>
              ) : (
                <>
                  <Link to="/browse" className="btn-white flex items-center gap-2 text-lg" data-testid="start-watching-button">
                    <Play className="w-5 h-5 fill-current" /> Start Watching
                  </Link>
                  <Link to="/creator/register" className="btn-secondary text-lg" data-testid="creator-button">
                    I'm a Creator
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Most Watched Section */}
      {mostWatched.length > 0 && (
        <section className="py-16 relative" data-testid="most-watched-section">
          <div className="w-full px-4 md:px-8 lg:px-[4%]">
            <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Most Watched on <span className="text-[#8B5CF6]">reframes</span>
            </h2>
            <div className="relative group/scroll">
              <button onClick={() => scroll(-1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-black -translate-x-1/2"
                data-testid="most-watched-prev">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scroll(1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-black translate-x-1/2"
                data-testid="most-watched-next">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div ref={scrollRef}
                className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory"
                style={{ scrollbarWidth: 'none' }}>
                {mostWatched.map((film, i) => (
                  <button key={film.film_id}
                    onClick={() => setPreviewFilm(film)}
                    className="shrink-0 w-56 md:w-64 group text-left snap-start"
                    data-testid={`most-watched-${film.film_id}`}>
                    <div className="relative rounded-xl overflow-hidden aspect-video mb-2.5 ring-1 ring-white/5 group-hover:ring-[#8B5CF6]/40 transition-all">
                      <img src={thumbUrl(film.thumbnail)} alt={film.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6">
                        <p className="text-base font-extrabold text-white line-clamp-2 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{film.title}</p>
                      </div>
                      {i < 3 && (
                        <span className="absolute top-2 left-2 text-[10px] bg-[#8B5CF6] px-2 py-0.5 rounded-full font-bold">
                          #{i + 1}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Coming Soon Section */}
      {(comingSoon.movies?.length > 0 || comingSoon.series?.length > 0) && (
        <section className="py-16 relative" data-testid="coming-soon-section">
          <div className="w-full px-4 md:px-8 lg:px-[4%]">
            <div className="flex items-center gap-3 mb-8">
              <Clock className="w-6 h-6 text-[#8B5CF6]" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Coming Soon
              </h2>
            </div>

            {comingSoon.movies?.length > 0 && (
              <div className="mb-10" data-testid="coming-soon-movies">
                <div className="flex items-center gap-2 mb-4">
                  <Film className="w-4 h-4 text-gray-400" />
                  <h3 className="text-base font-semibold text-gray-300">Movies</h3>
                  <span className="text-xs text-gray-600">{comingSoon.movies.length}</span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
                  {comingSoon.movies.map(film => {
                    const rd = film.release_date ? new Date(film.release_date + 'T00:00:00') : null;
                    return (
                      <button key={film.film_id} onClick={() => navigate(`/film/${film.film_id}`)}
                        className="shrink-0 w-[280px] min-w-[280px] group text-left relative rounded-lg overflow-hidden" data-testid={`coming-soon-card-${film.film_id}`}>
                        <div className="relative aspect-video">
                          <img src={thumbUrl(film.thumbnail)} alt={film.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          <div className="absolute top-2 left-2 z-20 flex rounded overflow-hidden text-[11px] font-bold" data-testid="coming-soon-badge">
                            <span className="px-2 py-1 bg-white text-black">Coming</span>
                            <span className="px-2 py-1 bg-[#8B5CF6] text-white">Soon</span>
                          </div>
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-8">
                            <h3 className="text-sm font-semibold text-white line-clamp-1">{film.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-400">{film.creator}</p>
                              {rd && (
                                <span className="flex items-center gap-1 text-xs text-gray-300">
                                  <Calendar className="w-3 h-3" />
                                  {rd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {comingSoon.series?.length > 0 && (
              <div data-testid="coming-soon-series">
                <div className="flex items-center gap-2 mb-4">
                  <Tv className="w-4 h-4 text-gray-400" />
                  <h3 className="text-base font-semibold text-gray-300">Series</h3>
                  <span className="text-xs text-gray-600">{comingSoon.series.length}</span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
                  {comingSoon.series.map(s => {
                    const rd = s.release_date ? new Date(s.release_date + 'T00:00:00') : null;
                    return (
                      <button key={s.film_id} onClick={() => navigate(`/film/${s.film_id}`)}
                        className="shrink-0 w-[280px] min-w-[280px] group text-left relative rounded-lg overflow-hidden" data-testid={`coming-soon-card-${s.film_id}`}>
                        <div className="relative aspect-video">
                          <img src={thumbUrl(s.thumbnail)} alt={s.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          <div className="absolute top-2 left-2 z-20 flex rounded overflow-hidden text-[11px] font-bold" data-testid="coming-soon-badge">
                            <span className="px-2 py-1 bg-white text-black">Coming</span>
                            <span className="px-2 py-1 bg-[#8B5CF6] text-white">Soon</span>
                          </div>
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 pt-8">
                            <h3 className="text-sm font-semibold text-white line-clamp-1">{s.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-400">{s.creator}</p>
                              {rd && (
                                <span className="flex items-center gap-1 text-xs text-gray-300">
                                  <Calendar className="w-3 h-3" />
                                  {rd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 relative z-10">
        <div className="w-full px-4 md:px-8 lg:px-[4%]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Why <span className="text-[#8B5CF6]">reframes</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index}
                className="admin-card text-center group hover:border-[#8B5CF6]/40 transition-all"
                data-testid={`feature-${index}`}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6] group-hover:bg-[#8B5CF6]/30 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Frequently Asked <span className="text-[#8B5CF6]">Questions</span>
          </h2>
          <div className="space-y-3">
            {[
              { q: 'What is Reframes?', a: 'Reframes is a streaming platform entirely dedicated to films created with artificial intelligence. Discover original stories made by independent filmmakers and AI creators from around the world, all in one place.' },
              { q: 'What kind of content can I watch on Reframes?', a: 'On Reframes you can find short films, series, experimental cinema, animated stories, and innovative AI productions created by emerging creators who are redefining the way stories are told.' },
              { q: 'Who creates the films on Reframes?', a: 'All films on Reframes are created by independent AI creators, filmmakers, and digital artists from around the world who are exploring a new era of cinema.' },
              { q: 'Can anyone publish a film on Reframes?', a: 'Yes. Reframes is designed to give creators from around the world a stage where they can showcase their AI films and reach a global audience.' },
              { q: 'Is Reframes free or paid?', a: 'Reframes offers two ways to access the platform: you can watch content for free with ads, or choose a paid subscription for an ad-free experience. By watching content on Reframes, you directly support creators, as a portion of the platform\'s revenue is distributed among the creators who publish their films.' },
            ].map((faq, i) => (
              <div key={i} data-testid={`faq-${i}`}
                className="border border-gray-800 rounded-lg overflow-hidden hover:border-[#8B5CF6]/30 transition-colors">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-white pr-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#8B5CF6] shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-60 pb-5' : 'max-h-0'}`}>
                  <p className="px-5 text-gray-400 leading-relaxed text-sm">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-[#8B5CF6]/10 to-transparent" />
        <div className="w-full px-4 md:px-8 lg:px-[4%] text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Ready to explore AI cinema?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join thousands of film enthusiasts discovering the future of storytelling
          </p>
          {!isAuthenticated && (
            <Link to="/browse" className="btn-primary text-lg px-8 inline-block" data-testid="cta-button">
              Start Your Journey
            </Link>
          )}
        </div>
      </section>

      {/* Preview Modal */}
      {previewFilm && (
        <ContentPreviewModal
          film={previewFilm}
          allFilms={mostWatched}
          onClose={() => setPreviewFilm(null)}
          onCreatorClick={(name) => { setPreviewFilm(null); setSelectedCreator(name); }}
        />
      )}

      {selectedCreator && (
        <CreatorProfileModal
          creatorName={selectedCreator}
          onClose={() => setSelectedCreator(null)}
        />
      )}
    </div>
  );
};

export default LandingPage;
